import { privateApi } from "./api";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getToken } from "./auth.service";

export interface FieldHub {
    macAddress: string;
    version: string;
    availableMemoryGB?: number;
    diskUsagePercent?: number;
    acquisitionSchedule?: string;
    batteryVoltage?: number;
    temperatureCelsius?: number;
    lastSeenAt?: string;
    createdAt: string;
    updatedAt: string;
    samples: FieldSample[];
}

export interface FieldSample {
    id: string;
    name: string;
    description?: string;
    projectId: string;
    scannerSerialNumber?: string;
    rotateClockwise: boolean;
    topEdgeDepthCm: number;
    tiltDeg: number;
    latitude: number | null;
    longitude: number | null;
    softDelete: boolean;
    createdAt: string;
    updatedAt: string;
    scanner?: {
        serialNumber: string;
        projectId: string;
        hubId?: string;
        mode: string;
        t: number;
        x: number;
        y: number;
        l: number;
        resolutionDpi: number;
        quality: number;
        cropTopMm: number;
        cropBottomMm: number;
        cropLeftMm: number;
        cropRightMm: number;
        createdAt: string;
        updatedAt: string;
    };
}

const getFieldData = async (projectId: string) => {
    return await privateApi.get<{ hubs: FieldHub[]; orphanedSamples: FieldSample[] }>(`/project/${projectId}/field`);
};

// Hook
export const useFieldData = (projectId?: string) => {
    return useQuery({
        queryKey: ['field-data', projectId],
        queryFn: () => {
            if (!projectId) return Promise.reject("Project ID missing");
            return getFieldData(projectId).then((response) => response.data);
        },
        enabled: !!projectId,
    });
};

// ==========================================================================
// Backoffice monitoring pages: the field grid, the bench grid, and the shared
// settings that decide which samples land on each.
// ==========================================================================

/**
 * `blue` marks something that turned up outside the schedule with a reason we
 * have — an extra acquisition, or a manual config wake-up, which acquires
 * nothing — `purple` a hub that came back off-schedule in its normal mode,
 * which nothing explains, and `none` a blank placeholder holding a column open
 * on a card that has nothing in it.
 */
export type DotStatus =
    | 'green'
    | 'yellow'
    | 'red'
    | 'blue'
    | 'purple'
    | 'none';

export interface MonitoringSlot {
    time: string;
    /** False for a column that no schedule asked for. */
    scheduled: boolean;
    matchedAt: string | null;
    json: DotStatus;
    jpg: DotStatus;
    jp2: DotStatus;
}

export interface SampleMonitoring {
    sampleId: string;
    sampleName: string;
    sampleDescription: string | null;
    projectId: string;
    projectName: string;
    scannerSerialNumber: string | null;
    /**
     * That scanner's acquisition resolution in DPI, editable here. The
     * backoffice is the only place it is shown: it is a device setting, not a
     * project one. Null when no scanner is wired to the sample.
     */
    scannerResolutionDpi: number | null;
    scheduleLabel: string | null;
    note: string | null;
    slots: MonitoringSlot[];
}

/** One expected wake-up and whether the hub reported a status for it. */
export interface HubSlot {
    time: string;
    /** False for a column that no schedule asked for. */
    scheduled: boolean;
    reportedAt: string | null;
    /** Whether this status came from someone waking the hub by hand. */
    configMode: boolean;
    status: DotStatus;
}

/**
 * One stretch of a hub's history and the acquisition schedule it ran on. A hub
 * whose schedule never changed has exactly one of these; a hub that was
 * re-scheduled has one per change, and the grid follows them.
 */
export interface ScheduleSegment {
    /** UTC ISO when this schedule took over; null for the opening stretch. */
    fromTime: string | null;
    cron: string;
    label: string | null;
}

export interface HubMonitoring {
    macAddress: string;
    version: string;
    availableMemoryGB: number | null;
    /**
     * The schedule the hub is set to run on, as cron — the setting itself,
     * which is what the card edits. `scheduleSegments` holds what the grid was
     * drawn against instead; the two differ until the hub picks a change up.
     */
    acquisitionSchedule: string;
    /**
     * Days between the hub's code updates — the `code_update_interval_days` of
     * its Hub.json, which the page can write back. Null on a hub whose config
     * does not carry the key.
     */
    codeUpdateIntervalDays: number | null;
    /**
     * The SIM in the hub's modem: the network it runs on and the ICCID printed
     * on the card. Both are ours to record — the hub reports neither — and both
     * are null until someone fills them in.
     */
    simOperator: string | null;
    simIccid: string | null;
    /**
     * The modem that SIM sits in, e.g. EG25G — ours to record like the two
     * above, and null until someone fills it in.
     */
    modem: string | null;
    /**
     * The branches this hub deploys itself from — the `scanorhize_pi_branch`
     * and `humeos_ansible_branch` of its Hub.json. Null on a hub whose config
     * does not carry the key yet, which the page shows as an empty field:
     * saving one writes the key for the first time.
     */
    scanorhizePiBranch: string | null;
    humeosAnsibleBranch: string | null;
    /**
     * What the modem last reported being connected to: the network it actually
     * registered on and the APN it dialled. Unlike `simOperator` above, which
     * is what we wrote down when the SIM was fitted, these come from the
     * device — the two disagreeing is worth seeing. Null when the hub does not
     * report them or could not read the modem.
     */
    modemOperator: string | null;
    modemApn: string | null;
    batteryVoltage: number | null;
    temperatureCelsius: number | null;
    /** Modem signal strength in percent, from the hub's latest status. */
    modemSignalQuality: number | null;
    lastSeenAt: string | null;
    nextWakeUp: string | null;
    /**
     * Whether that wake-up lands on the acquisition schedule. False means the
     * hub plans to come back when nothing is expected of it; null when there is
     * nothing to judge.
     */
    nextWakeUpOnSchedule: boolean | null;
    /** The schedules this hub ran on over the grid's history, oldest first. */
    scheduleSegments: ScheduleSegment[];
    scheduleLabel: string | null;
    note: string | null;
    /** Same slot grid as this hub's samples, one dot per expected wake-up. */
    slots: HubSlot[];
    samples: SampleMonitoring[];
}

/** What the hub settings dialog reads and writes: the Hubs page has it too. */
export type HubSettingsSource = Pick<
    HubMonitoring,
    | 'macAddress'
    | 'codeUpdateIntervalDays'
    | 'simOperator'
    | 'simIccid'
    | 'modem'
    | 'modemOperator'
    | 'modemApn'
    | 'scanorhizePiBranch'
    | 'humeosAnsibleBranch'
>;

export interface ProjectMonitoring {
    projectId: string;
    projectName: string;
    /**
     * Our own shorthand for this project, for the internal team only. It means
     * nothing to the project's members — it is not a name they chose or would
     * recognise — so it belongs to the backoffice Field page and nowhere else.
     * Never render it in a user-facing view.
     */
    internalTeamOnlyLabel: string | null;
    /** A hub with scanners in several projects appears under each of them. */
    hubs: HubMonitoring[];
    orphanedSamples: SampleMonitoring[];
}

/**
 * Which monitoring page a sample shows on. FIELD is a device planted out on a
 * real site, BENCH one kept at the office for development or pre-production
 * testing; the two get their own pages because they are read for different
 * reasons. Null means the sample is not monitored at all.
 */
export type MonitoringSite = 'FIELD' | 'BENCH';

export interface SettingsSample {
    id: string;
    name: string;
    monitoring: MonitoringSite | null;
}

export interface ProjectSamples {
    projectId: string;
    projectName: string;
    samples: SettingsSample[];
}

const getMonitoring = async (site: MonitoringSite) => {
    const { data } = await privateApi.get<ProjectMonitoring[]>(`/monitoring`, {
        params: { site },
    });
    return data;
};

/**
 * One site's monitoring grid — the field devices or the ones on the bench.
 * Each hub's history is drawn against the schedule it reported at the time
 * rather than the one it is set to now, so raising a hub's rate does not redden
 * the past — see `scheduleSegments`.
 */
export const useMonitoring = (site: MonitoringSite) => {
    return useQuery({
        queryKey: ['monitoring', 'grid', site],
        queryFn: () => getMonitoring(site),
        enabled: !!getToken(),
    });
};

const getMonitoringSamples = async () => {
    return await privateApi.get<ProjectSamples[]>(`/monitoring/samples`);
};

export const useMonitoringSamples = () => {
    return useQuery({
        queryKey: ['monitoring', 'samples'],
        queryFn: getMonitoringSamples,
        enabled: !!getToken(),
    });
};

const setMonitoring = async ({
    sampleId,
    monitoring,
}: {
    sampleId: string;
    monitoring: MonitoringSite | null;
}) => {
    return await privateApi.patch(`/monitoring/samples/${sampleId}`, {
        monitoring,
    });
};

const setInternalTeamOnlyLabel = async ({
    projectId,
    label,
}: {
    projectId: string;
    label: string;
}) => {
    return await privateApi.patch(
        `/monitoring/projects/${projectId}/internal-team-only-label`,
        { label },
    );
};

const setScannerResolution = async ({
    serialNumber,
    resolutionDpi,
}: {
    serialNumber: string;
    resolutionDpi: number;
}) => {
    return await privateApi.patch(
        `/monitoring/scanners/${serialNumber}/resolution`,
        { resolutionDpi },
    );
};

/**
 * Backoffice-only: set a scanner's acquisition resolution. The write lands in
 * the database and in the hub's scanner config on S3, so the device picks it up
 * on its next config read.
 */
export const useSetScannerResolution = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: setScannerResolution,
        // Settled rather than success: a scanner wired to no hub has its row
        // written and then the config push refused, so a refused save still
        // leaves something new to read.
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['monitoring', 'grid'] });
            queryClient.invalidateQueries({ queryKey: ['backOfficeScanners'] });
        },
    });
};

/** Backoffice-only: rename our internal shorthand for a project. */
export const useSetInternalTeamOnlyLabel = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: setInternalTeamOnlyLabel,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['monitoring', 'grid'],
            });
        },
    });
};

/**
 * Move a sample between the monitoring pages, or off them. Both grids are
 * invalidated on the prefix, since a move takes the sample off one page and
 * puts it on the other.
 */
export const useSetMonitoring = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: setMonitoring,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['monitoring', 'samples'] });
            queryClient.invalidateQueries({
                queryKey: ['monitoring', 'grid'],
            });
        },
    });
};
